import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Plus, Users, Clock, Calendar, Wallet, Trash2, Download, Package, Wrench, FileText, Moon, PartyPopper, CalendarDays, ChevronLeft, ChevronRight, Shield, AlertTriangle, Activity, Coffee, Save, PauseCircle, Building2, MessageSquare } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function WorkJournalPage() {
  const { isRole, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [journalData, setJournalData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [diaries, setDiaries] = useState([]);
  const [projects, setProjects] = useState([]);

  // Dialogs
  const [showBulkAttendance, setShowBulkAttendance] = useState(false);
  const [showBulkLeave, setShowBulkLeave] = useState(false);
  const [showBulkExpense, setShowBulkExpense] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);

  // Annotazioni manuali
  const [annotations, setAnnotations] = useState({ fermo: false, fermo_motivo: '', cassa_integrazione: false, cassa_integrazione_note: '', note_generali: '', employee_notes: {} });
  const [savingAnnotations, setSavingAnnotations] = useState(false);

  // Forms
  const [attendanceForm, setAttendanceForm] = useState({ date: '', clock_in: '08:00', break_start: '12:00', break_end: '13:00', clock_out: '17:00', site_id: '', notes: '' });
  const [leaveForm, setLeaveForm] = useState({ request_type: 'ferie', start_date: '', end_date: '', reason: '', status: 'approvata' });
  const [expenseForm, setExpenseForm] = useState({ expense_date: '', expense_type: 'carburante', amount: '', description: '', site_id: '' });
  const [availabilityForm, setAvailabilityForm] = useState({ date: '', availability_type: 'straordinario', notes: '', employee_id: '' });

  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');

  const load = async () => {
    setLoading(true);
    try {
      const [journalRes, empRes, sitesRes, diariesRes, projRes] = await Promise.all([
        api.get(`/daily-journal/${selectedDate}`).catch(() => ({ data: null })),
        api.get('/bulk-operations/employees-for-selection').catch(() => ({ data: [] })),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/production-diary', { params: { date: selectedDate } }).catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setJournalData(journalRes.data);
      setEmployees(empRes.data);
      setSites(sitesRes.data);
      setDiaries(Array.isArray(diariesRes.data) ? diariesRes.data : []);
      setProjects(projRes.data || []);
      // Carica annotazioni manuali
      if (journalRes.data?.annotations) {
        setAnnotations(journalRes.data.annotations);
      } else {
        setAnnotations({ fermo: false, fermo_motivo: '', cassa_integrazione: false, cassa_integrazione_note: '', note_generali: '', employee_notes: {} });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedDate]);

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const downloadPDF = async () => {
    try {
      const response = await api.get(`/daily-journal/${selectedDate}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `giornale_lavoro_${selectedDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore download PDF');
    }
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployees(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  const handleBulkAttendance = async () => {
    if (selectedEmployees.length === 0) { alert('Seleziona almeno un dipendente'); return; }
    try {
      await api.post('/bulk-operations/attendance', { employee_ids: selectedEmployees, ...attendanceForm });
      setShowBulkAttendance(false); setSelectedEmployees([]); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleBulkLeave = async () => {
    if (selectedEmployees.length === 0) { alert('Seleziona almeno un dipendente'); return; }
    try {
      await api.post('/bulk-operations/leaves', { employee_ids: selectedEmployees, ...leaveForm });
      setShowBulkLeave(false); setSelectedEmployees([]); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleBulkExpense = async () => {
    if (selectedEmployees.length === 0) { alert('Seleziona almeno un dipendente'); return; }
    try {
      await api.post('/bulk-operations/expenses', { employee_ids: selectedEmployees, ...expenseForm, amount: parseFloat(expenseForm.amount) || 0 });
      setShowBulkExpense(false); setSelectedEmployees([]); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleCreateAvailability = async () => {
    try {
      const payload = { ...availabilityForm };
      if (canManage && availabilityForm.employee_id && availabilityForm.employee_id !== 'self') {
        payload.employee_id = availabilityForm.employee_id;
      } else { delete payload.employee_id; }
      await api.post('/availability', payload);
      setShowAvailability(false);
      setAvailabilityForm({ date: selectedDate, availability_type: 'straordinario', notes: '', employee_id: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteAvailability = async (id) => {
    if (!window.confirm('Eliminare questa reperibilita?')) return;
    try { await api.delete(`/availability/${id}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm('Eliminare questa presenza?')) return;
    try { await api.delete(`/attendance/${id}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleSaveAnnotations = async () => {
    setSavingAnnotations(true);
    try {
      await api.put(`/daily-journal/${selectedDate}/annotations`, annotations);
    } catch (err) { alert(err.response?.data?.detail || 'Errore salvataggio annotazioni'); }
    finally { setSavingAnnotations(false); }
  };

  const setEmployeeNote = (empId, note) => {
    setAnnotations(prev => ({ ...prev, employee_notes: { ...prev.employee_notes, [empId]: note } }));
  };

  const setAllEmployeesNote = (note) => {
    const empNotes = {};
    (journalData?.attendance || []).forEach(a => { if (a.employee_id) empNotes[a.employee_id] = note; });
    setAnnotations(prev => ({ ...prev, employee_notes: { ...prev.employee_notes, ...empNotes } }));
  };

  const availabilityTypeLabels = {
    straordinario: { label: 'Straordinario', icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200' },
    notturno: { label: 'Notturno', icon: Moon, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    sabato: { label: 'Sabato', icon: CalendarDays, color: 'bg-amber-100 text-amber-700 border-amber-200' },
    domenica: { label: 'Domenica', icon: CalendarDays, color: 'bg-orange-100 text-orange-700 border-orange-200' },
    festivo: { label: 'Festivo', icon: PartyPopper, color: 'bg-red-100 text-red-700 border-red-200' },
    straordinario_notturno: { label: 'Notturno', icon: Moon, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    straordinario_festivo: { label: 'Festivo', icon: PartyPopper, color: 'bg-pink-100 text-pink-700 border-pink-200' },
    straordinario_weekend: { label: 'Weekend', icon: CalendarDays, color: 'bg-orange-100 text-orange-700 border-orange-200' }
  };

  const pdoStatusColors = { aperto: 'bg-green-50 text-green-700 border-green-300', chiuso: 'bg-slate-100 text-slate-700', sospeso: 'bg-amber-50 text-amber-700 border-amber-300' };
  const summary = journalData?.summary || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="work-journal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Giornale Lavoro</h1>
          <p className="text-sm text-slate-500 mt-1">Resoconto completo della giornata: presenze, attivita, materiali, PDO, anomalie</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="prev-day"><ChevronLeft size={18} /></Button>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" data-testid="date-selector" />
          <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="next-day"><ChevronRight size={18} /></Button>
          <Button onClick={downloadPDF} className="bg-red-600 hover:bg-red-700" data-testid="download-pdf-btn">
            <Download size={16} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards - 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-3 text-center">
            <Users size={18} className="mx-auto text-green-600 mb-1" />
            <p className="text-xl font-bold text-green-700">{summary.total_employees_present || 0}</p>
            <p className="text-xs text-green-600">Presenti</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 text-center">
            <Clock size={18} className="mx-auto text-blue-600 mb-1" />
            <p className="text-xl font-bold text-blue-700">{summary.total_hours_worked || 0}h</p>
            <p className="text-xs text-blue-600">Ore Lavorate</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-3 text-center">
            <Coffee size={18} className="mx-auto text-slate-600 mb-1" />
            <p className="text-xl font-bold text-slate-700">{summary.total_break_minutes || 0}m</p>
            <p className="text-xs text-slate-600">Pause Tot.</p>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50/50">
          <CardContent className="p-3 text-center">
            <Activity size={18} className="mx-auto text-teal-600 mb-1" />
            <p className="text-xl font-bold text-teal-700">{summary.total_activities || 0}</p>
            <p className="text-xs text-teal-600">Attivita</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-3 text-center">
            <Shield size={18} className="mx-auto text-amber-600 mb-1" />
            <p className="text-xl font-bold text-amber-700">{summary.total_active_pdos || 0}</p>
            <p className="text-xs text-amber-600">PDO Attivi</p>
          </CardContent>
        </Card>
        <Card className={`border-red-200 ${(summary.total_incidents || 0) > 0 ? 'bg-red-50/50' : 'bg-slate-50/50'}`}>
          <CardContent className="p-3 text-center">
            <AlertTriangle size={18} className={`mx-auto mb-1 ${(summary.total_incidents || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`} />
            <p className={`text-xl font-bold ${(summary.total_incidents || 0) > 0 ? 'text-red-700' : 'text-slate-400'}`}>{summary.total_incidents || 0}</p>
            <p className="text-xs text-slate-500">Anomalie</p>
          </CardContent>
        </Card>
      </div>

      {/* Annotazioni Giornata - Fermo, CIG, Note */}
      {canManage && (
        <Card className="border-orange-200 bg-orange-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><MessageSquare size={16} className="text-orange-600" /> Annotazioni Giornata</CardTitle>
              <Button size="sm" onClick={handleSaveAnnotations} disabled={savingAnnotations} className="bg-orange-600 hover:bg-orange-700">
                <Save size={14} className="mr-1" /> {savingAnnotations ? 'Salvo...' : 'Salva'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Fermo */}
              <div className="space-y-2 p-3 rounded-lg border bg-white">
                <div className="flex items-center gap-2">
                  <Checkbox checked={annotations.fermo} onCheckedChange={(v) => setAnnotations(prev => ({...prev, fermo: !!v}))} id="fermo-check" />
                  <Label htmlFor="fermo-check" className="flex items-center gap-1 text-sm font-semibold text-red-700"><PauseCircle size={15} /> Fermo Lavoro</Label>
                </div>
                {annotations.fermo && (
                  <Textarea placeholder="Motivo del fermo (es: maltempo, mancanza materiali, guasto...)" value={annotations.fermo_motivo} onChange={(e) => setAnnotations(prev => ({...prev, fermo_motivo: e.target.value}))} className="text-sm" rows={2} data-testid="fermo-motivo" />
                )}
              </div>
              {/* Cassa Integrazione */}
              <div className="space-y-2 p-3 rounded-lg border bg-white">
                <div className="flex items-center gap-2">
                  <Checkbox checked={annotations.cassa_integrazione} onCheckedChange={(v) => setAnnotations(prev => ({...prev, cassa_integrazione: !!v}))} id="cig-check" />
                  <Label htmlFor="cig-check" className="flex items-center gap-1 text-sm font-semibold text-purple-700"><Building2 size={15} /> Cassa Integrazione Aperta</Label>
                </div>
                {annotations.cassa_integrazione && (
                  <Textarea placeholder="Note CIG (es: CIG ordinaria per 3 dipendenti...)" value={annotations.cassa_integrazione_note} onChange={(e) => setAnnotations(prev => ({...prev, cassa_integrazione_note: e.target.value}))} className="text-sm" rows={2} data-testid="cig-note" />
                )}
              </div>
            </div>
            {/* Note generali */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Note Generali Giornata</Label>
              <Textarea placeholder="Annotazioni libere sulla giornata lavorativa..." value={annotations.note_generali} onChange={(e) => setAnnotations(prev => ({...prev, note_generali: e.target.value}))} className="text-sm" rows={2} data-testid="note-generali" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manager Actions */}
      {canManage && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users size={16} /> Azioni Rapide - Seleziona Dipendenti ({selectedEmployees.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1 mb-3 max-h-32 overflow-y-auto">
              {employees.map(emp => (
                <div key={emp.id} onClick={() => toggleEmployee(emp.id)}
                  className={`px-2 py-1 rounded text-xs cursor-pointer transition-all flex items-center gap-1 ${
                    selectedEmployees.includes(emp.id) ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}>
                  <Checkbox checked={selectedEmployees.includes(emp.id)} className="h-3 w-3" />
                  {emp.last_name} {emp.first_name?.charAt(0)}.
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => { setAttendanceForm({ ...attendanceForm, date: selectedDate }); setShowBulkAttendance(true); }}
                className="bg-green-600 hover:bg-green-700" disabled={selectedEmployees.length === 0}>
                <Clock size={14} className="mr-1" /> Presenza
              </Button>
              <Button size="sm" onClick={() => { setLeaveForm({ ...leaveForm, start_date: selectedDate, end_date: selectedDate }); setShowBulkLeave(true); }}
                className="bg-amber-600 hover:bg-amber-700" disabled={selectedEmployees.length === 0}>
                <Calendar size={14} className="mr-1" /> Ferie/Permesso
              </Button>
              <Button size="sm" onClick={() => { setExpenseForm({ ...expenseForm, expense_date: selectedDate }); setShowBulkExpense(true); }}
                className="bg-purple-600 hover:bg-purple-700" disabled={selectedEmployees.length === 0}>
                <Wallet size={14} className="mr-1" /> Rimborso
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="presenze">
        <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="presenze" className="text-xs md:text-sm" data-testid="tab-presenze">Presenze ({journalData?.attendance?.length || 0})</TabsTrigger>
          <TabsTrigger value="attivita" className="text-xs md:text-sm" data-testid="tab-attivita">Attivita ({journalData?.activities?.length || 0})</TabsTrigger>
          <TabsTrigger value="pdo" className="text-xs md:text-sm" data-testid="tab-pdo">PDO ({journalData?.active_pdos?.length || 0})</TabsTrigger>
          <TabsTrigger value="magazzino" className="text-xs md:text-sm" data-testid="tab-magazzino">Magazzino ({(journalData?.inventory?.loads?.length || 0) + (journalData?.inventory?.unloads?.length || 0)})</TabsTrigger>
          <TabsTrigger value="attrezzature" className="text-xs md:text-sm" data-testid="tab-attrezzature">Attrezzature ({journalData?.equipment_used?.length || 0})</TabsTrigger>
          <TabsTrigger value="anomalie" className="text-xs md:text-sm" data-testid="tab-anomalie">Anomalie ({journalData?.incidents?.length || 0})</TabsTrigger>
          <TabsTrigger value="progetti" className="text-xs md:text-sm" data-testid="tab-progetti">Progetti ({diaries.length})</TabsTrigger>
          <TabsTrigger value="reperibilita" className="text-xs md:text-sm" data-testid="tab-reperibilita">Reperibilita ({journalData?.availability?.length || 0})</TabsTrigger>
        </TabsList>

        {/* PRESENZE TAB - con pause */}
        <TabsContent value="presenze">
          {canManage && (
            <div className="flex items-center gap-2 mb-2 mt-3">
              <Input className="h-8 text-xs flex-1 max-w-md" placeholder="Scrivi una nota e premi il bottone per applicarla a tutti i dipendenti..." id="bulk-note" data-testid="bulk-note-input" />
              <Button size="sm" variant="outline" onClick={() => { const note = document.getElementById('bulk-note').value; if(note) { setAllEmployeesNote(note); document.getElementById('bulk-note').value=''; }}} className="text-xs"><Users size={14} className="mr-1" /> Applica Nota a Tutti</Button>
            </div>
          )}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dipendente</TableHead>
                    <TableHead>Entrata</TableHead>
                    <TableHead>Pausa</TableHead>
                    <TableHead>Uscita</TableHead>
                    <TableHead>Ore</TableHead>
                    <TableHead className="hidden md:table-cell">Cantiere</TableHead>
                    {canManage && <TableHead>Nota Ore</TableHead>}
                    {canManage && <TableHead className="w-16">Az.</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!journalData?.attendance?.length ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessuna presenza registrata per questa data</TableCell></TableRow>
                  ) : journalData.attendance.map(a => (
                    <TableRow key={a.id} className="hover:bg-slate-50">
                      <TableCell>
                        <p className="font-medium text-sm">{a.employee_name || '-'}</p>
                        {a.employee_role && <p className="text-xs text-slate-400">{a.employee_role}</p>}
                      </TableCell>
                      <TableCell className="text-sm">{a.clock_in_display || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {a.break_start ? (
                          <span>
                            {a.break_start_display}
                            {a.break_end ? ` - ${a.break_end_display}` : ' (in corso)'}
                            {a.break_minutes != null && <span className="text-xs text-slate-400 ml-1">({a.break_minutes}min)</span>}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-sm">{a.clock_out_display || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className={`${parseFloat(a.hours_worked||0) < 8 ? 'bg-amber-50 text-amber-700 border-amber-200' : parseFloat(a.hours_worked||0) > 8 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-green-50 text-green-700'}`}>{a.hours_worked || 0}h</Badge></TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-600">{a.site_name || '-'}</TableCell>
                      {canManage && (
                        <TableCell className="p-1">
                          <Input
                            className="h-7 text-xs min-w-[120px]"
                            placeholder={parseFloat(a.hours_worked||0) !== 8 ? 'Motivo ore diverse...' : 'Nota opzionale'}
                            value={annotations.employee_notes?.[a.employee_id] || ''}
                            onChange={(e) => setEmployeeNote(a.employee_id, e.target.value)}
                          />
                        </TableCell>
                      )}
                      {canManage && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAttendance(a.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ATTIVITA TAB (dal diario produzione) */}
        <TabsContent value="attivita">
          <Card className="border-teal-200 shadow-sm mt-3">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>PDL</TableHead>
                    <TableHead>Descrizione Attivita</TableHead>
                    <TableHead className="text-right">Ore</TableHead>
                    <TableHead className="text-right">Personale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!journalData?.activities?.length ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessuna attivita registrata. Le attivita vengono auto-compilate dal Diario Produzione.</TableCell></TableRow>
                  ) : journalData.activities.map((act, i) => (
                    <TableRow key={i} className="hover:bg-teal-50/50">
                      <TableCell className="text-sm font-medium">{act.site_name || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-purple-50 text-purple-700">{act.numero_pdl || '-'}</Badge></TableCell>
                      <TableCell className="text-sm">{act.description || '-'}</TableCell>
                      <TableCell className="text-right"><Badge variant="outline" className="bg-blue-50 text-blue-700">{act.hours_worked || 0}h</Badge></TableCell>
                      <TableCell className="text-right text-sm">{act.personnel_count || 0} pers.</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PDO TAB */}
        <TabsContent value="pdo">
          <Card className="border-amber-200 shadow-sm mt-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Shield size={16} className="text-amber-600" /> PDO Attivi - Permessi di Lavoro</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {!journalData?.active_pdos?.length ? (
                <p className="text-center py-8 text-slate-400 text-sm">Nessun PDO attivo. I PDO vengono gestiti dalla pagina Progetti.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numero</TableHead>
                      <TableHead>Progetto</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Lavoratori</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {journalData.active_pdos.map(pdo => (
                      <TableRow key={pdo.id} className="hover:bg-amber-50/50">
                        <TableCell className="font-bold text-sm">#{pdo.numero || '---'}</TableCell>
                        <TableCell className="text-sm">{pdo.project_name || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs bg-slate-50">{pdo.tipo?.replace('_', ' ') || '-'}</Badge></TableCell>
                        <TableCell className="text-sm">{pdo.area_lavoro || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-xs ${pdoStatusColors[pdo.status] || ''}`}>{pdo.status}</Badge></TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(pdo.workers || []).map(w => <Badge key={w.id} variant="outline" className="text-xs bg-blue-50 text-blue-700">{w.name}</Badge>)}
                            {(!pdo.workers || pdo.workers.length === 0) && <span className="text-xs text-slate-400">-</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MAGAZZINO TAB */}
        <TabsContent value="magazzino">
          <div className="space-y-4 mt-3">
            <Card className="border-emerald-200">
              <CardHeader className="pb-2 bg-emerald-50">
                <CardTitle className="text-sm text-emerald-700 flex items-center gap-2"><Package size={16} /> Carichi ({journalData?.inventory?.loads?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Articolo</TableHead><TableHead>Quantita</TableHead><TableHead>Fornitore</TableHead><TableHead>Rif. Fattura</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {!journalData?.inventory?.loads?.length ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Nessun carico</TableCell></TableRow>
                    ) : journalData.inventory.loads.map(m => (
                      <TableRow key={m.id} className="hover:bg-emerald-50/50">
                        <TableCell className="font-medium">{m.item_name}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700">+{m.quantity}</Badge></TableCell>
                        <TableCell>{m.supplier || '-'}</TableCell>
                        <TableCell className="text-sm">{m.invoice_reference || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardHeader className="pb-2 bg-red-50">
                <CardTitle className="text-sm text-red-700 flex items-center gap-2"><Package size={16} /> Scarichi ({journalData?.inventory?.unloads?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Articolo</TableHead><TableHead>Quantita</TableHead><TableHead>Motivo</TableHead><TableHead>Cantiere</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {!journalData?.inventory?.unloads?.length ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Nessuno scarico</TableCell></TableRow>
                    ) : journalData.inventory.unloads.map(m => (
                      <TableRow key={m.id} className="hover:bg-red-50/50">
                        <TableCell className="font-medium">{m.item_name}</TableCell>
                        <TableCell><Badge className="bg-red-100 text-red-700">-{m.quantity}</Badge></TableCell>
                        <TableCell className="text-sm">{m.reason || '-'}</TableCell>
                        <TableCell>{m.site_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ATTREZZATURE TAB */}
        <TabsContent value="attrezzature">
          <Card className="border-cyan-200 shadow-sm mt-3">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Attrezzatura</TableHead><TableHead>Ore</TableHead><TableHead>Operatore</TableHead><TableHead>N. PDL</TableHead><TableHead className="hidden md:table-cell">Cantiere</TableHead></TableRow></TableHeader>
                <TableBody>
                  {!journalData?.equipment_used?.length ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessuna attrezzatura utilizzata</TableCell></TableRow>
                  ) : journalData.equipment_used.map((eq, i) => (
                    <TableRow key={i} className="hover:bg-cyan-50/50">
                      <TableCell className="font-medium flex items-center gap-2"><Wrench size={14} className="text-cyan-600" />{eq.equipment_name}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-cyan-50 text-cyan-700">{eq.hours_used}h</Badge></TableCell>
                      <TableCell className="text-sm">{eq.operator_name || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-purple-50 text-purple-700">{eq.pdl_reference || '-'}</Badge></TableCell>
                      <TableCell className="hidden md:table-cell">{eq.site_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANOMALIE TAB */}
        <TabsContent value="anomalie">
          <Card className="border-red-200 shadow-sm mt-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle size={16} className="text-red-600" /> Anomalie e Segnalazioni</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {!journalData?.incidents?.length ? (
                <p className="text-center py-8 text-slate-400 text-sm">Nessuna anomalia o segnalazione per questa data</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Cantiere</TableHead><TableHead>Descrizione</TableHead><TableHead>Segnalato da</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {journalData.incidents.map((inc, i) => (
                      <TableRow key={i} className="hover:bg-red-50/50">
                        <TableCell><Badge variant="outline" className="bg-red-50 text-red-700">{inc.incident_type || inc.type || '-'}</Badge></TableCell>
                        <TableCell className="text-sm">{inc.site_name || '-'}</TableCell>
                        <TableCell className="text-sm">{inc.description || '-'}</TableCell>
                        <TableCell className="text-sm">{inc.reporter_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* PROGETTI / DIARI PRODUZIONE TAB */}
        <TabsContent value="progetti">
          <Card className="border-indigo-200 shadow-sm mt-3">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Wrench size={18} /> Diari di Produzione del {selectedDate}</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {diaries.length === 0 ? (
                <p className="text-center py-8 text-slate-400 text-sm">Nessun diario di produzione per questa data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cantiere</TableHead>
                      <TableHead>Progetto</TableHead>
                      <TableHead>Avanzamento</TableHead>
                      <TableHead>Ore</TableHead>
                      <TableHead>Personale</TableHead>
                      <TableHead>Materiali</TableHead>
                      <TableHead>Attrezzature</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diaries.map(d => {
                      const site = sites.find(s => s.id === d.site_id);
                      const proj = projects.find(p => p.id === d.project_id);
                      const progColor = proj ? (proj.progress_percentage >= 75 ? 'bg-green-500' : proj.progress_percentage >= 50 ? 'bg-blue-500' : proj.progress_percentage >= 25 ? 'bg-amber-500' : 'bg-red-500') : 'bg-slate-300';
                      return (
                        <TableRow key={d.id} className="hover:bg-slate-50">
                          <TableCell className="text-sm font-medium">{site?.name || d.site_id || '-'}</TableCell>
                          <TableCell className="text-sm">{proj?.name || '-'}</TableCell>
                          <TableCell>
                            {proj && (
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div className={`h-full ${progColor} rounded-full`} style={{ width: `${proj.progress_percentage || 0}%` }} />
                                </div>
                                <span className="text-xs font-medium">{proj.progress_percentage || 0}%</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{d.hours_worked || 0}h</TableCell>
                          <TableCell className="text-sm">{d.personnel_count || (d.personnel_ids?.length || 0)}</TableCell>
                          <TableCell className="text-sm">{d.materials?.length || 0}</TableCell>
                          <TableCell className="text-sm">{d.equipment_used?.length || 0}</TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{d.operational_notes || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPERIBILITA TAB */}
        <TabsContent value="reperibilita">
          <Card className="border-orange-200 shadow-sm mt-3">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Moon size={18} /> Reperibilita del {selectedDate}</CardTitle>
              <Button size="sm" onClick={() => { setAvailabilityForm({ ...availabilityForm, date: selectedDate }); setShowAvailability(true); }} className="bg-orange-600 hover:bg-orange-700">
                <Plus size={14} className="mr-1" /> Aggiungi
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Dipendente</TableHead><TableHead>Tipo</TableHead><TableHead>Note</TableHead><TableHead className="w-16">Az.</TableHead></TableRow></TableHeader>
                <TableBody>
                  {!journalData?.availability?.length ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">Nessuna reperibilita dichiarata</TableCell></TableRow>
                  ) : journalData.availability.map(av => {
                    const typeInfo = availabilityTypeLabels[av.availability_type] || { label: av.availability_type, color: 'bg-slate-100' };
                    const TypeIcon = typeInfo.icon || Moon;
                    const canDelete = canManage || av.employee_id === user?.employee_id;
                    return (
                      <TableRow key={av.id} className="hover:bg-orange-50/50">
                        <TableCell className="font-medium">{av.employee_name}</TableCell>
                        <TableCell><Badge variant="outline" className={typeInfo.color}><TypeIcon size={12} className="mr-1" />{typeInfo.label}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-600">{av.notes || '-'}</TableCell>
                        <TableCell>{canDelete && <Button variant="ghost" size="sm" onClick={() => handleDeleteAvailability(av.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Attendance Dialog */}
      <Dialog open={showBulkAttendance} onOpenChange={setShowBulkAttendance}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registra Presenza</DialogTitle><DialogDescription>{selectedEmployees.length} dipendenti selezionati</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={attendanceForm.date} onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Entrata</Label><Input type="time" value={attendanceForm.clock_in} onChange={(e) => setAttendanceForm({ ...attendanceForm, clock_in: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Uscita</Label><Input type="time" value={attendanceForm.clock_out} onChange={(e) => setAttendanceForm({ ...attendanceForm, clock_out: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Inizio Pausa</Label><Input type="time" value={attendanceForm.break_start} onChange={(e) => setAttendanceForm({ ...attendanceForm, break_start: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Fine Pausa</Label><Input type="time" value={attendanceForm.break_end} onChange={(e) => setAttendanceForm({ ...attendanceForm, break_end: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={attendanceForm.site_id || 'none'} onValueChange={(v) => setAttendanceForm({ ...attendanceForm, site_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBulkAttendance(false)}>Annulla</Button><Button onClick={handleBulkAttendance} className="bg-green-600 hover:bg-green-700">Registra</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Leave Dialog */}
      <Dialog open={showBulkLeave} onOpenChange={setShowBulkLeave}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registra Ferie/Permesso</DialogTitle><DialogDescription>{selectedEmployees.length} dipendenti selezionati</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Tipo</Label><Select value={leaveForm.request_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, request_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ferie">Ferie</SelectItem><SelectItem value="permesso">Permesso</SelectItem><SelectItem value="malattia">Malattia</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Motivo</Label><Input value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBulkLeave(false)}>Annulla</Button><Button onClick={handleBulkLeave} className="bg-amber-600 hover:bg-amber-700">Registra</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Expense Dialog */}
      <Dialog open={showBulkExpense} onOpenChange={setShowBulkExpense}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registra Rimborso</DialogTitle><DialogDescription>{selectedEmployees.length} dipendenti selezionati</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={expenseForm.expense_date} onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Importo (EUR)</Label><Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tipo</Label><Select value={expenseForm.expense_type} onValueChange={(v) => setExpenseForm({ ...expenseForm, expense_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="carburante">Carburante</SelectItem><SelectItem value="vitto">Vitto</SelectItem><SelectItem value="alloggio">Alloggio</SelectItem><SelectItem value="materiale">Materiale</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Input value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBulkExpense(false)}>Annulla</Button><Button onClick={handleBulkExpense} className="bg-purple-600 hover:bg-purple-700">Registra</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={showAvailability} onOpenChange={setShowAvailability}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aggiungi Reperibilita</DialogTitle><DialogDescription>Dichiara la disponibilita per straordinari</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={availabilityForm.date} onChange={(e) => setAvailabilityForm({ ...availabilityForm, date: e.target.value })} /></div>
            {canManage && (
              <div className="space-y-1.5"><Label>Dipendente</Label><Select value={availabilityForm.employee_id || 'self'} onValueChange={(v) => setAvailabilityForm({ ...availabilityForm, employee_id: v })}><SelectTrigger><SelectValue placeholder="Me stesso" /></SelectTrigger><SelectContent><SelectItem value="self">Me stesso</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}</SelectContent></Select></div>
            )}
            <div className="space-y-1.5"><Label>Tipo</Label><Select value={availabilityForm.availability_type} onValueChange={(v) => setAvailabilityForm({ ...availabilityForm, availability_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="straordinario">Straordinario</SelectItem><SelectItem value="notturno">Notturno</SelectItem><SelectItem value="sabato">Sabato</SelectItem><SelectItem value="domenica">Domenica</SelectItem><SelectItem value="festivo">Festivo</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={availabilityForm.notes} onChange={(e) => setAvailabilityForm({ ...availabilityForm, notes: e.target.value })} placeholder="Es: Disponibile dalle 20:00" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAvailability(false)}>Annulla</Button><Button onClick={handleCreateAvailability} className="bg-orange-600 hover:bg-orange-700">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
